import { writable, get } from 'svelte/store';
import type { Listing } from '../types';
import type { Filters } from './filters';
import { buildFeedQuery } from './filters';
import { api } from '../api';

export interface FeedState {
  items: Listing[];
  cursor: string; // server next_cursor; '' ⇒ no more pages
  loading: boolean;
  error: string | null;
  reloadToken: number; // bumped on each full (re)load — Feed.svelte scrolls to top on change
}

const EMPTY: FeedState = { items: [], cursor: '', loading: false, error: null, reloadToken: 0 };
export const feed = writable<FeedState>({ ...EMPTY });

let generation = 0; // guards against out-of-order responses across reloads

export async function loadFeed(f: Filters): Promise<void> {
  const mine = ++generation;
  feed.update((s) => ({ ...s, loading: true, error: null }));
  try {
    const res = await api.feed(buildFeedQuery(f));
    if (mine !== generation) return;
    feed.update((s) => ({
      ...s,
      items: res.items,
      cursor: res.next_cursor,
      loading: false,
      reloadToken: s.reloadToken + 1,
    }));
  } catch (e) {
    if (mine !== generation) return;
    feed.update((s) => ({ ...s, loading: false, error: (e as Error).message }));
  }
}

export async function loadMore(f: Filters): Promise<void> {
  const s = get(feed);
  if (s.loading || s.cursor === '') return;
  const mine = generation;
  feed.update((x) => ({ ...x, loading: true }));
  try {
    const res = await api.feed(buildFeedQuery(f, s.cursor));
    if (mine !== generation) return;
    feed.update((x) => ({
      ...x,
      items: [...x.items, ...res.items],
      cursor: res.next_cursor,
      loading: false,
    }));
  } catch (e) {
    if (mine !== generation) return;
    feed.update((x) => ({ ...x, loading: false, error: (e as Error).message }));
  }
}

export function patchItem(id: number, patch: Partial<Listing>): void {
  feed.update((s) => ({
    ...s,
    items: s.items.map((it) => (it.id === id ? { ...it, ...patch } : it)),
  }));
}

export function resetFeed(): void {
  generation++;
  feed.set({ ...EMPTY });
}
